import { type ReactElement } from "react";
import { Route, Routes } from "react-router-dom";

import { AssistantAccessProvider, useAssistantAccess } from "@/feature-flags";
import { AssistantAdminPage } from "@/features/assistant/admin";
import {
  ACCEPT_INVITATION_ROUTE,
  AcceptInvitationPage,
  SessionProvider,
} from "@/features/organization";
import { AppLayout } from "@/layout";
import { AuthProvider } from "@/lib/AuthProvider";
import { BrowserObservabilityBootstrap } from "@/observability";
import {
  AuditLogPage,
  ConversationLogsPage,
  Dashboard,
  DevicesPage,
  EntitlementsPage,
  FeatureFlagsPage,
  FeedbackPage,
  GuardrailMetricsPage,
  NotFound,
  ObservabilityPage,
  OrganizationPage,
  RagQualityPage,
  Settings,
  TestInboxPage,
  UsageMetricsPage,
  UsersPage,
  WaitlistPage,
} from "@/pages";

/**
 * Renders Assistant routes only when the `assistant-console` flag is on for
 * this environment; otherwise falls through to the 404 page so a disabled
 * route is unreachable even via direct navigation. Mirrors the sidebar gating.
 */
function RequireAssistant({ children }: { children: ReactElement }): ReactElement {
  return useAssistantAccess() ? children : <NotFound />;
}

export function App(): React.ReactElement {
  return (
    <AuthProvider>
      <SessionProvider>
        <BrowserObservabilityBootstrap />
        <AssistantAccessProvider>
          <Routes>
            <Route element={<AppLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/observability" element={<ObservabilityPage />} />
              <Route path="/waitlist" element={<WaitlistPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/organization" element={<OrganizationPage />} />
              <Route path="/devices" element={<DevicesPage />} />
              <Route path="/feature-flags" element={<FeatureFlagsPage />} />
              <Route path="/entitlements" element={<EntitlementsPage />} />
              <Route
                path="/assistant"
                element={
                  <RequireAssistant>
                    <AssistantAdminPage />
                  </RequireAssistant>
                }
              />
              <Route
                path="/usage-metrics"
                element={
                  <RequireAssistant>
                    <UsageMetricsPage />
                  </RequireAssistant>
                }
              />
              <Route path="/audit-log" element={<AuditLogPage />} />
              <Route
                path="/conversation-logs"
                element={
                  <RequireAssistant>
                    <ConversationLogsPage />
                  </RequireAssistant>
                }
              />
              <Route
                path="/guardrail-metrics"
                element={
                  <RequireAssistant>
                    <GuardrailMetricsPage />
                  </RequireAssistant>
                }
              />
              <Route
                path="/rag-quality"
                element={
                  <RequireAssistant>
                    <RagQualityPage />
                  </RequireAssistant>
                }
              />
              <Route path="/test-inbox" element={<TestInboxPage />} />
              <Route path="/test-inbox/:env" element={<TestInboxPage />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path={ACCEPT_INVITATION_ROUTE} element={<AcceptInvitationPage />} />
          </Routes>
        </AssistantAccessProvider>
      </SessionProvider>
    </AuthProvider>
  );
}
